import React, { useEffect, useState } from 'react';
import { customStorage, formatDateTime, getPayloadData, isInRole, isNullOrEmptyString } from '../../services/UtilityService';
import CustomTable from '../../share-components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CATEGORY_CREATE } from '../../constants/routeConstants';
import { Button, Card, Container } from 'react-bootstrap';
import { useAppContext } from '../../share-components/AppContext';
import AlertDialog from '../../share-components/AlertDialog';
import { editCategory, getCategories, toggleCategoryActivate } from '../../services/ManagementService';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import SearchColumnSelect from '../../share-components/SearchColumnSelect';
import FormTitle from '../../share-components/FormTitle';
import * as Yup from 'yup';
import CommonDialog from '../../share-components/CommonDialog';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import NoDataFound from '../../share-components/NoDataFound';
import { RG_ADMIN_ONLY } from '../../constants/roleConstants';

const CategoryList = () => {

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { setLoading, setRouteNaviData, setShowApiErrorMessage } = useAppContext();
    const [categoryListData, setCategoryListData] = useState(null);
    const [showToggleActivateConfirmDialog, setShowToggleActivateConfirmDialog] = useState(false);
    const [actionTargetCategory, setActionTargetCategory] = useState(null);
    const [showEditCategoryForm, setShowEditCategoryForm] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filteredData, setFilteredData] = useState();
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const navigate = useNavigate();

    const fetchCategoryList = async () => {
        try {
            setLoading(true);
            const response = await getCategories();
            if (response) {
                const convertedResponse = (response || [])
                    .sort((a, b) => b.lastUpdatedDateTime?.localeCompare(a.lastUpdatedDateTime))
                    .map((categoryData) => ({
                        ...categoryData,
                        active: categoryData?.deactivateFlag === false ? "YES" : "NO",
                        updatedDateTime: formatDateTime(categoryData?.lastUpdatedDateTime?.slice(0, 8))
                    }));
                setCategoryListData(convertedResponse);
            }
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("CATEGORY >> ALL CATEGORIES");
        fetchCategoryList();
    }, []);

    const handleToggleActivateClick = (userAccount) => {
        setActionTargetCategory(userAccount);
        setShowToggleActivateConfirmDialog(true);
    }

    const handleEditCategoryClick = (category) => {
        setActionTargetCategory(category);
        setShowEditCategoryForm(true);
    }

    const initialValues = {
        categoryName: "",
    };

    const validationSchema = Yup.object({
        categoryName: Yup.string()
            .max(30, 'Category Name must not exceed 30 characters.')
            .required('Category Name is required.'),
    });

    const onSubmit = async (form) => {
        setLoading(true);
        try {
            const requestData = {
                categoryId: actionTargetCategory?.categoryId,
                categoryName: form.categoryName
            }
            await editCategory(requestData);
            setShowEditCategoryForm(false);
            setSuccessMessage("Category is Successfully Edited.");
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            Header: 'Category Name',
            accessor: 'categoryName',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Active',
            accessor: 'active',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Last Updated Date',
            accessor: 'updatedDateTime',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Edit Category',
            accessor: 'editCategory',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center',
            Cell: ({ row }) => (
                <Button variant='secondary'
                    onClick={() => handleEditCategoryClick(row.original)}
                    disabled={!isInRole(RG_ADMIN_ONLY, currentUser?.role)}
                    style={{
                        padding: "0px 5px",
                        margin: "5px"
                    }}
                >
                    <i className="bi bi-pencil-square"></i>
                </Button>
            )
        },
        {
            Header: 'Activate/Deactivate',
            accessor: 'toggleActivate',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center',
            style: {
                maxWidth: "200px"
            },
            Cell: ({ row }) => (
                row.original.deactivateFlag === false ? <Button variant='danger'
                    onClick={() => handleToggleActivateClick(row.original)}
                    disabled={!isInRole(RG_ADMIN_ONLY, currentUser?.role)}
                    style={{
                        padding: "0px 5px",
                        margin: "5px"
                    }}
                >
                    <i className="bi bi-lock"></i>
                    Deactivate
                </Button> :
                    <Button variant='primary'
                        onClick={() => handleToggleActivateClick(row.original)}
                        disabled={!isInRole(RG_ADMIN_ONLY, currentUser?.role)}
                        style={{
                            padding: "0px 5px",
                            margin: "5px"
                        }}
                    >
                        <i className="bi bi-unlock"></i>
                        ReActivate
                    </Button>
            )
        }
    ];

    const goToCreateCategory = () => {
        navigate(ROUTE_CATEGORY_CREATE);
    }

    const handleToggleActivate = async (form) => {
        setLoading(true);
        try {
            setShowToggleActivateConfirmDialog(false);
            const response = await toggleCategoryActivate(actionTargetCategory?.categoryId);
            setSuccessMessage(`Category is Successfully ${actionTargetCategory?.deactivateFlag === false ? "Deactivated" : "Activated"}.`);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setShowToggleActivateConfirmDialog(false);
        setShowEditCategoryForm(false);
        setSuccessMessage(null);
        setActionTargetCategory(null);
        await fetchCategoryList();
    }

    const handleCancelToggleActivate = () => {
        setShowToggleActivateConfirmDialog(false);
        setTimeout(() => {
            setActionTargetCategory(null);
        }, 200);
    }

    const renderEditCategory = (
        <Container style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '30px', fontWeight: 'normal' }}>

            {/* Edit Category Form */}
            <Card style={{ width: '100%', maxWidth: '400px', padding: '20px', margin: '0 auto' }}>
                <Card.Body>
                    <FormTitle title={"Edit Category Name"} isCenter={true} />
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                        enableReinitialize={true}>
                        {({ errors, isValid, dirty }) => {
                            return (
                                <Form>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Old Category Name</label>
                                        <div className="w-100 label-box">
                                            {actionTargetCategory?.categoryName}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">New Category Name*</label>
                                        <Field name="categoryName" type='text' className="form-control customInputControl" />
                                        <ErrorMessage name="categoryName" component="div" className="text-danger" />
                                    </div>

                                    <Button
                                        className="w-100 mb-3"
                                        type="submit"
                                        variant="primary"
                                        disabled={!(isValid && dirty)}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        className="w-100 mb-3"
                                        type="button"
                                        variant="secondary"
                                        onClick={() => { setShowEditCategoryForm(false); setActionTargetCategory(null) }}
                                    >
                                        Cancel
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                </Card.Body>
            </Card>
        </Container>
    );

    return (
        <div id='categoryListPage'>
            <FormTitle title={"Category List"} isCenter={true} />
            {isFirstLoaded && <>
                <div style={{ overflow: "auto", paddingBottom: "50px" }}>
                    <div>
                        <div style={{ minWidth: "max-content", maxWidth: '650px', margin: '0 auto' }}>
                            <Card>
                                <Card.Body style={{ padding: '10px 5px 20px 5px' }}>
                                    <div>
                                        {isInRole(RG_ADMIN_ONLY, currentUser?.role) && <div style={{ display: 'inline-block' }}>
                                            <Button type='button' variant='warning' className='btn-sm' onClick={goToCreateCategory}><i className="bi bi-plus-circle"></i> Add New Category</Button>
                                        </div>}
                                        <div style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                            <div style={{ display: 'inline-block' }}>
                                                <SearchColumnSelect data={categoryListData} setData={setFilteredData} columns={columns} />
                                            </div>

                                        </div>
                                        {filteredData?.length > 0 ?
                                            <CustomTable
                                                tableId='categoryListTbl'
                                                columns={columns}
                                                tblData={filteredData || []}
                                                showPagination={true}
                                                itemsPerPage={20}
                                            // initialSortBy={[{ id: 'updatedDateTime', desc: false }]}
                                            /> : <NoDataFound />}
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>
                <CommonDialog
                    dialogId='categoryEditDialog'
                    show={showEditCategoryForm}
                    noCloseBtn={false}
                    onHide={() => { setShowEditCategoryForm(false); setActionTargetCategory(null); }}
                    size={'fullscreen'}
                    body={renderEditCategory}
                />
                <ConfirmDialog
                    dialogId='toggleActivateDialog'
                    showDialog={showToggleActivateConfirmDialog}
                    // title='Logout Dialog'
                    body={`Are you sure to ${actionTargetCategory?.deactivateFlag === false ? "Deactivate" : "Activate"} the [${actionTargetCategory?.categoryName}] category?`}
                    onConfirm={handleToggleActivate}
                    onCancel={handleCancelToggleActivate}
                />
                <AlertDialog
                    dialogId={'successAlertDialog'}
                    showDialog={!isNullOrEmptyString(successMessage)}
                    body={successMessage}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default CategoryList;